import { useRef, useState } from "react";
import { toast } from "sonner";
import { PortalModal, Field, DemoNotice } from "@/components/portal/PortalModal";
import { IN_STATES } from "@/config/regions";
import { registerPartner, newIdempotencyKey, isLive } from "@/lib/platformClient";
import { useDialogs } from "@/lib/dialogs";

const KINDS = ["Venue", "Supplier", "Partner"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const PartnerDialog = ({ onClose, kind = "Partner" }) => {
  const open = useDialogs();
  const key = useRef(newIdempotencyKey());
  const [f, setF] = useState({ kind, business: "", state: "TG", city: "", contact: "", email: "", services: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy || result || f.website) return;
    const errs = {};
    if (f.business.trim().length < 2) errs.business = "Business name is required.";
    if (!f.city.trim()) errs.city = "City is required.";
    if (f.contact.trim().length < 2) errs.contact = "Contact name is required.";
    if (!EMAIL.test(f.email.trim())) errs.email = "Enter a valid email.";
    if (!f.consent) errs.consent = "Consent is required.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const payload = { partner_type: f.kind.toUpperCase(), business_name: f.business.trim(), location: { country: "IN", state_code: f.state, city: f.city.trim() }, contact: { name: f.contact.trim(), email: f.email.trim() }, services: f.services.trim(), consent: { contact: true, captured_at: new Date().toISOString() }, source: "ayndri-portal" };
      setResult(await registerPartner(payload, key.current));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <PortalModal title="Partner interest received" onClose={onClose} testId="partner-dialog">
        <div className="done" data-testid="partner-result">
          <div className="done-check">✓</div>
          <h3>{result.mode === "demo" ? "Demo registration preview" : "Registration submitted"}</h3>
          <p>Reference: <b data-testid="partner-reference">{result.reference}</b></p>
          {result.mode === "demo" && <DemoNotice>No supplier, venue or partner record was created. Approval and records belong to the existing supplier/partner services.</DemoNotice>}
          <div className="modal-actions center"><button className="btn blue" onClick={onClose} data-testid="partner-done-button">Close</button></div>
        </div>
      </PortalModal>
    );
  }

  return (
    <PortalModal title="Join the Ayndri Partner Network" onClose={onClose} testId="partner-dialog">
      {!isLive("partnerRegistration") && <DemoNotice>Registrations are previewed only. Live records are created and approved in the existing canonical supplier/partner services, never in a separate database.</DemoNotice>}
      <form onSubmit={submit} noValidate style={{ marginTop: 14 }}>
        <input className="hp" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" name="website" />
        <div className="optionrow three" role="radiogroup" aria-label="Partner type">
          {KINDS.map((k) => (
            <button type="button" key={k} role="radio" aria-checked={f.kind === k} className={`option ${f.kind === k ? "selected" : ""}`} onClick={() => setF({ ...f, kind: k })} data-testid={`partner-kind-${k.toLowerCase()}`}><b>{k}</b></button>
          ))}
        </div>
        <div className="formgrid" style={{ marginTop: 14 }}>
          <Field id="prt-business" label="Business name *" error={errors.business}><input id="prt-business" value={f.business} onChange={set("business")} maxLength={80} data-testid="partner-business-input" /></Field>
          <Field id="prt-state" label="State"><select id="prt-state" value={f.state} onChange={set("state")} data-testid="partner-state-select">{IN_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></Field>
          <Field id="prt-city" label="City *" error={errors.city}><input id="prt-city" value={f.city} onChange={set("city")} maxLength={60} data-testid="partner-city-input" /></Field>
          <Field id="prt-contact" label="Contact name *" error={errors.contact}><input id="prt-contact" value={f.contact} onChange={set("contact")} maxLength={80} data-testid="partner-contact-input" /></Field>
          <Field id="prt-email" label="Email *" error={errors.email}><input id="prt-email" type="email" value={f.email} onChange={set("email")} maxLength={120} data-testid="partner-email-input" /></Field>
          <Field id="prt-services" label={f.kind === "Venue" ? "Capacity & facilities" : "Services offered"}><input id="prt-services" value={f.services} onChange={set("services")} maxLength={200} data-testid="partner-services-input" /></Field>
        </div>
        <label className="checkrow"><input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="partner-consent-checkbox" /> I consent to be contacted about partner onboarding.</label>
        {errors.consent && <small className="err" data-testid="partner-consent-error">{errors.consent}</small>}
        <div className="modal-actions">
          <button type="button" className="btn outline" onClick={() => open("onboard")} data-testid="partner-planner-instead-button">I'm an event planner</button>
          <button type="submit" className="btn blue" disabled={busy} data-testid="partner-submit-button">{busy ? "Submitting…" : "Submit interest"}</button>
        </div>
      </form>
    </PortalModal>
  );
};
